import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useIntl } from 'react-intl';
import { merge } from 'lodash-es';
import ListPageHeader from '../../_shared/components/ListPageHeader';
import ReducerConstants from '../../_shared/constants/reducers';
import usePaymentsOnHoldSummary from '../../_shared/hooks/activity/usePaymentsOnHoldSummary';
import usePaymentsOnHoldMetadata from '../../_shared/hooks/activity/usePaymentsOnHoldMetadata';
import usePaymentsOnHoldTradeList from '../../_shared/hooks/activity/usePaymentsOnHoldTradeList';
import useEtrPaymentsOnHoldTradeList from '../../_shared/hooks/activity/useEtrPaymentsOnHoldTradeList';
import ROUTES from '../../components/Routes/constants';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import { StyledPage, StyledBody, StyledFooter } from './Styles';
import scrollToTop from '../../_shared/helpers/scrollToTop';
import useAnalytics from '../../_shared/hooks/useAnalytics';
import {
  PaymentsOnHoldContext,
  paymentsOnHoldContextInitialState,
  PaymentsOnHoldFilter,
  PaymentsOnHoldList,
  PaymentsOnHoldSummaryBox
} from '../../components/PaymentsOnHold';
import SegmentConfig from '../../_shared/constants/segmentConfig';

const PaymentsOnHold = ({
  isOmp,
  handleNavigateToTradeUri,
  isMergedPaymentsOnHoldEnabled
}) => {
  const intl = useIntl();
  const [sortSelected, setSortSelected] = useState(0);
  const [filterSelected, setFilterSelected] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [legacyOffset, setLegacyOffset] = useState(0);
  const [etrOffset, setEtrOffset] = useState(0);
  const [offsetHistory, setOffsetHistory] = useState({
    [currentPage]: { etrOffset, legacyOffset }
  });
  const tradesOnPage = 50;
  const track = useAnalytics();

  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const redirectToHomePage = () => {
    window.location.href = ROUTES.TR4_ENDPOINT;
  };

  useEffect(() => {
    if (user && !(user?.isCanSell || user?.isAccountant)) {
      redirectToHomePage();
    }
  }, [user]);

  const metadata = usePaymentsOnHoldMetadata();
  const {
    etrPaymentsOnHold,
    refetchEtrPaymentsOnHold
  } = useEtrPaymentsOnHoldTradeList({
    isMergedPaymentsOnHoldEnabled
  });
  const tradeListParams = {
    sort: {
      id: sortSelected
    },
    dataFilter: {
      id: filterSelected
    },
    pagination: {
      from: isMergedPaymentsOnHoldEnabled
        ? legacyOffset
        : (currentPage - 1) * tradesOnPage,
      size: tradesOnPage
    }
  };
  const paymentsOnHoldTradeList = usePaymentsOnHoldTradeList(
    tradeListParams,
    etrPaymentsOnHold,
    {
      isMergedPaymentsOnHoldEnabled,
      etrOffset,
      tradesOnPage,
      province: user?.province
    }
  );

  const handleSortTrades = selectedId => {
    const label = metadata.sortDefinitions.inputs.find(
      item => item.key === selectedId
    ).value;

    track(MixpanelConfig.names.sortPaymentsOnHold, {
      source: label
    });
    track(SegmentConfig.names.paymentsOnHoldSort, {});

    setSortSelected(selectedId);
    setCurrentPage(1);

    if (isMergedPaymentsOnHoldEnabled) {
      setLegacyOffset(0);
      setEtrOffset(0);
    }
  };

  const handleFilterTrades = (_event, _itemSelected, index) => {
    const label = metadata.filterDefinitions.inputs.find(
      item => item.key === index
    ).value;

    track(MixpanelConfig.names.filterPaymentsOnHold, {
      source: label
    });
    track(SegmentConfig.names.paymentsOnHoldFilter, {});

    setFilterSelected(index);
    setCurrentPage(1);

    if (isMergedPaymentsOnHoldEnabled) {
      setLegacyOffset(0);
      setEtrOffset(0);
    }
  };

  const handleClickNextPage = () => {
    setCurrentPage(currentPage + 1);
    scrollToTop(isOmp);

    if (isMergedPaymentsOnHoldEnabled) {
      setLegacyOffset(
        prevOffset =>
          prevOffset +
            paymentsOnHoldTradeList?.tradeSummaries?.filter(
              ({ isEtrTrade }) => !isEtrTrade
            ).length || 0
      );
      setEtrOffset(
        prevOffset =>
          prevOffset +
            paymentsOnHoldTradeList?.tradeSummaries?.filter(
              ({ isEtrTrade }) => isEtrTrade
            ).length || 0
      );
      setOffsetHistory(prevOffSetHistory => ({
        ...prevOffSetHistory,
        [currentPage]: { etrOffset, legacyOffset }
      }));
    }
  };

  const handleClickPrevPage = () => {
    setCurrentPage(currentPage - 1);
    scrollToTop(isOmp);

    if (isMergedPaymentsOnHoldEnabled) {
      setEtrOffset(offsetHistory[currentPage - 1]?.etrOffset);
      setLegacyOffset(offsetHistory[currentPage - 1]?.legacyOffset);
    }
  };

  const context = merge(paymentsOnHoldContextInitialState, {
    metadata,
    summary: usePaymentsOnHoldSummary(etrPaymentsOnHold, {
      isMergedPaymentsOnHoldEnabled
    }),
    trades: paymentsOnHoldTradeList,
    sortSelected,
    filterSelected,
    currentPage,
    tradesOnPage,
    onSortTrades: handleSortTrades,
    onFilterTrades: handleFilterTrades,
    onClickNextPage: handleClickNextPage,
    onClickPrevPage: handleClickPrevPage,
    onEtrSubmitDocuments: refetchEtrPaymentsOnHold
  });

  const mainHeading = intl.formatMessage({ id: 'list.header.paymentsOnHold' });
  const subHeading = intl.formatMessage({ id: 'list.header.vehicles' });
  const caption = intl.formatMessage({
    id: 'list.header.paymentsOnHold.caption'
  });
  const footerLabelText = intl.formatMessage({
    id: 'list.footer.label.paymentsOnHold'
  });
  const footerButtonText = intl.formatMessage({
    id: 'list.footer.button.backToDashboard'
  });
  const footerButtons = isOmp
    ? []
    : [{ text: footerButtonText, onClick: redirectToHomePage }];

  return (
    <PaymentsOnHoldContext.Provider value={context}>
      <StyledPage isOmp={isOmp}>
        <ListPageHeader
          mainHeading={mainHeading}
          subHeading={`${
            Number.isInteger(context.summary.totalNumberOfTrades)
              ? context.summary.totalNumberOfTrades
              : ''
          } ${subHeading}`}
          caption={caption}
        />
        <PaymentsOnHoldSummaryBox />
        <StyledBody>
          <PaymentsOnHoldFilter />
          <PaymentsOnHoldList
            handleNavigateToTradeUri={handleNavigateToTradeUri}
          />
        </StyledBody>
        <StyledFooter label={footerLabelText} buttons={footerButtons} />
      </StyledPage>
    </PaymentsOnHoldContext.Provider>
  );
};

export default PaymentsOnHold;
